"use client"

/**
 * Shared marketing-site header — dark cinema chrome used across every
 * public route (home, /pricing, /learn, etc.). Matches the
 * visual idiom of the legacy inline header in app/page.tsx (VelovaLogo,
 * velova-btn CTAs, hairline underline-on-hover nav) but is CMS-agnostic:
 * a later task imports this into every marketing route, including a
 * rebuilt app/page.tsx.
 */

import Link from "next/link"
import { usePathname } from "next/navigation"
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { AnimatePresence, motion } from "framer-motion"
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react"
import { VelovaLogo } from "@/components/velova-logo"
import { cn } from "@/lib/utils"

/* Fixed bar height. Also the offset the mobile panel hangs from. */
const HEADER_H = 76

interface NavItem {
  label: string
  href: string
}

interface NavGroup {
  label: string
  items: NavItem[]
}

const PRIMARY_LINKS: NavItem[] = [
  { label: "How it works", href: "/how-it-works" },
]

/* Standalone marketing routes. These are the links that previously only
   existed in the footer's "Pages" column. */
const SITE_LINKS: NavItem[] = [
  { label: "Actions", href: "/actions" },
  { label: "Pricing", href: "/pricing" },
  { label: "Contact", href: "/contact" },
]

/* Homepage section anchors — the footer's "On this page" column. Absolute
   (`/#id`) rather than bare hashes so they still resolve from /privacy,
   /about and every other route that renders this shared header. */
const SECTION_LINKS: NavItem[] = [
  { label: "AI models", href: "/#coverage" },
  { label: "How it works", href: "/#method" },
  { label: "Features", href: "/#features" },
]

const SOLUTIONS_GROUP: NavGroup = {
  label: "Solutions",
  items: [
    { label: "Marketing teams", href: "/for-marketing-teams" },
    { label: "Agencies", href: "/agencies" },
  ],
}

const PRICING_LINK: NavItem = { label: "Pricing", href: "/pricing" }

const LEARN_GROUP: NavGroup = {
  label: "Learn",
  items: [
    { label: "What is GEO?", href: "/what-is-geo" },
    { label: "GEO vs SEO", href: "/geo-vs-seo" },
    { label: "AI models", href: "/ai-models" },
    { label: "Glossary", href: "/geo-glossary" },
    { label: "Industry guides", href: "/learn" },
  ],
}

/* ─────────────────────────────────────────────────────────────
   Desktop nav link — hairline underline on hover, matches the
   original TopNav's link treatment.
   ───────────────────────────────────────────────────────────── */
function NavLink({ href, label }: NavItem) {
  const pathname = usePathname()
  const active = pathname === href

  return (
    <Link
      href={href}
      aria-current={active ? "page" : undefined}
      className={cn(
        "text-[13.5px] leading-none px-3 py-2 rounded-full transition-colors whitespace-nowrap",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/50",
        // White only — no accent colour. `!` is load-bearing: on /actions,
        // /pricing and /contact this header renders INSIDE .vx-root, whose
        // `.vx-root a { color:#7fd9ec }` (0,1,1) out-specifies a plain text-
        // utility (0,1,0) and turned every link cyan. Literal white rather
        // than --velova-ink/--velova-blue because `main.old-landing`
        // rescopes those tokens on the homepage. Between them, those two
        // page-scoped systems are why this bar never looked the same twice.
        // The current page is the ONLY highlighted item.
        active
          ? "!text-white bg-white/[0.12] ring-1 ring-inset ring-white/20"
          : "!text-white/70 hover:!text-white hover:bg-white/[0.06]",
      )}
    >
      {label}
    </Link>
  )
}

/* ─────────────────────────────────────────────────────────────
   Desktop dropdown — click-to-open, closes on outside click,
   Escape, or item selection. Used for Solutions and Learn.
   ───────────────────────────────────────────────────────────── */
function NavDropdown({ group }: { group: NavGroup }) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("pointerdown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        className="flex items-center gap-1 text-[13.5px] text-[var(--velova-ink)] hover:opacity-60 transition-opacity rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--velova-blue)]"
      >
        {group.label}
        <ChevronDown
          className={cn("w-3.5 h-3.5 transition-transform duration-200", open && "rotate-180")}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.16, ease: [0.2, 0.8, 0.2, 1] }}
            className="absolute left-1/2 -translate-x-1/2 top-full mt-3 min-w-[200px] rounded-[var(--velova-radius-sm)] border border-white/10 bg-[var(--velova-paper-3)] shadow-[0_20px_50px_-20px_rgba(0,0,0,0.8)] py-2 z-50"
          >
            {group.items.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                className="block px-4 py-2.5 text-[13.5px] text-[var(--velova-ink)] hover:bg-white/[0.06] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--velova-blue)]"
              >
                {item.label}
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────
   Mobile accordion group — same links, stacked disclosure.
   ───────────────────────────────────────────────────────────── */
function MobileGroup({ group, onNavigate }: { group: NavGroup; onNavigate: () => void }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b border-white/8">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between min-h-[44px] py-3 velova-display text-[28px] leading-[1.1] text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--velova-blue)] rounded-sm"
      >
        {group.label}
        <ChevronDown className={cn("w-5 h-5 shrink-0 transition-transform duration-200", open && "rotate-180")} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-1 pb-4">
              {group.items.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={onNavigate}
                  className="min-h-[44px] flex items-center text-[16px] text-white/70 hover:text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--velova-blue)] rounded-sm"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [mounted, setMounted] = useState(false)
  const pathname = usePathname()

  useEffect(() => setMounted(true), [])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : ""
    return () => {
      document.body.style.overflow = ""
    }
  }, [mobileOpen])

  return (
    <header
      className={cn(
        "sticky top-0 z-40 transition-colors duration-300",
        scrolled
          ? "bg-[rgba(7,9,13,0.92)] backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent",
      )}
    >
      {/* Geometry is FIXED — identical padding and logo size on every page and
          at every scroll position, so the bar never shifts or resizes as you
          navigate. Only the background fades in on scroll. */}
      <div className="velova-container flex items-center justify-between"
        style={{ height: HEADER_H }}>
        <Link
          href="/"
          className="flex items-center gap-3 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white/50"
        >
          {/* velova-light.png has 229px of transparent padding on its 1983px
              canvas (11.55%), so the visible mark sits ~14px inside its box at
              this width. Pull it back so the logo is OPTICALLY on the same
              128px left edge as the page content and the footer logo, rather
              than merely having its bounding box there. */}
          <VelovaLogo variant="light" width={120} className="-ml-[14px]" />
        </Link>

        <nav
          aria-label="Main"
          className="hidden md:flex items-center gap-6 lg:gap-7"
        >
          {SITE_LINKS.map((item) => (
            <NavLink key={item.href} {...item} />
          ))}
          <span aria-hidden="true" className="w-px h-3.5 bg-white/15" />
          {SECTION_LINKS.map((item) => (
            <NavLink key={item.href} {...item} />
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link href="/signup" className="hidden sm:inline-flex items-center gap-2 h-10 px-5 rounded-full text-[13px] font-medium whitespace-nowrap !text-black bg-white hover:bg-white/90 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/60">
            Start free
            <ArrowRight className="w-[14px] h-[14px]" />
          </Link>
          <button
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/15 hover:border-white/40 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--velova-blue)]"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Mobile panel — PORTALLED to <body>.
          It must not live inside <header>: once scrolled, the header gains
          `backdrop-blur-xl`, and a non-none backdrop-filter makes an element
          a containing block for position:fixed descendants. The panel's
          `inset-0` then resolved against the header's 76px box instead of the
          viewport, so it rendered 390x76 — every link clipped away and only
          the trailing "Start free" visible. At scroll 0 the header is
          transparent (no blur), which is why it only broke mid-page. */}
      {mounted &&
        createPortal(
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              key="mobile-drawer"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
              className="fixed inset-0 z-[55] md:hidden"
            >
              {/* Scrim: dims the page behind the panel and closes it on an
                  outside tap. */}
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
                className="absolute inset-0 w-full h-full bg-black/60 cursor-default"
              />
              <div
                className="absolute left-0 right-0 px-4"
                style={{ top: HEADER_H }}
              >
              <div className="rounded-2xl border border-white/12 bg-[#0a0d13] backdrop-blur-xl shadow-[0_28px_60px_-24px_rgba(0,0,0,0.9)] overflow-hidden">
                <nav className="px-4 py-2 max-h-[65vh] overflow-y-auto" aria-label="Main">
                  {[...SITE_LINKS, ...SECTION_LINKS].map((item, i) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() => setMobileOpen(false)}
                      className={cn(
                        // No corner radius on the row: combined with `border-t`
                        // the radius curls the divider up at both ends, which
                        // reads as stray rounded rectangles inside the panel.
                        // Explicit rgba too — `border-white/8` was resolving to
                        // Tailwind's opaque default border colour (cream).
                        "min-h-[48px] flex items-center text-[16px] px-2 transition-colors",
                        i > 0 && "border-t border-t-[rgba(255,255,255,0.10)]",
                        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-white/50",
                        pathname === item.href ? "!text-white" : "!text-white/70 hover:!text-white",
                      )}
                    >
                      {item.label}
                    </Link>
                  ))}
                </nav>
                <div className="p-3 pt-1">
                  <Link
                    href="/signup"
                    onClick={() => setMobileOpen(false)}
                    className="inline-flex w-full items-center justify-center gap-2 min-h-[48px] px-5 rounded-full text-[14px] font-medium !text-black bg-white hover:bg-white/90 transition-colors"
                  >
                    Start free
                    <ArrowRight className="w-[14px] h-[14px]" />
                  </Link>
                </div>
              </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
,
          document.body,
        )}
    </header>
  )
}
